import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip } from '@mui/material';

const FORMATS = [
    { key: 'bold', label: 'Bold', icon: 'B', marker: '*', shortcut: 'Ctrl+B', fontWeight: 700 },
    { key: 'italic', label: 'Italic', icon: 'I', marker: '_', shortcut: 'Ctrl+I', fontStyle: 'italic' },
    { key: 'strikethrough', label: 'Strikethrough', icon: 'S', marker: '~', shortcut: 'Ctrl+Shift+X', textDecoration: 'line-through' },
    { key: 'monospace', label: 'Monospace', icon: '</>', marker: '```', shortcut: 'Ctrl+Shift+M', fontFamily: 'monospace', fontSize: '11px' },
];

const FormattingToolbar = ({ textareaRef, value, onChange, isMobile, isCompact }) => {
    const [expanded, setExpanded] = useState(false);

    // Collapsed = show a single "Aa" toggle that reveals B / I / S / </> on click.
    // Mobile uses this pattern; on medium desktop widths (isCompact) we use the
    // same pattern, but sized to match the desktop 28 px icon buttons instead of
    // the 44 px touch targets mobile needs.
    const collapsed = isMobile || isCompact;
    const toggleBtnSize = isMobile ? '44px' : '28px';
    const toggleBtnRadius = isMobile ? '50%' : '6px';
    const toggleBtnFontSize = isMobile ? '14px' : '13px';

    const applyFormat = (marker) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = value.substring(start, end);

        let newValue;
        let cursorPos;

        if (selectedText) {
            newValue = value.substring(0, start) + marker + selectedText + marker + value.substring(end);
            cursorPos = end + marker.length * 2;
        } else {
            newValue = value.substring(0, start) + marker + marker + value.substring(end);
            cursorPos = start + marker.length;
        }

        onChange(newValue);
        requestAnimationFrame(() => {
            textarea.focus();
            if (selectedText) {
                textarea.selectionStart = start;
                textarea.selectionEnd = cursorPos;
            } else {
                textarea.selectionStart = cursorPos;
                textarea.selectionEnd = cursorPos;
            }
        });
    };

    const formatButtons = FORMATS.map((fmt) => (
        <Tooltip key={fmt.key} title={`${fmt.label} (${fmt.shortcut})`} arrow>
            <IconButton
                size="small"
                onClick={() => applyFormat(fmt.marker)}
                sx={{
                    width: isMobile ? '36px' : '28px',
                    height: isMobile ? '36px' : '28px',
                    borderRadius: '6px',
                    fontSize: fmt.fontSize || '13px',
                    fontWeight: fmt.fontWeight || 400,
                    fontStyle: fmt.fontStyle || 'normal',
                    fontFamily: fmt.fontFamily || 'inherit',
                    textDecoration: fmt.textDecoration || 'none',
                    color: '#637381',
                    '&:hover': {
                        backgroundColor: 'rgba(145, 158, 171, 0.16)',
                        color: '#212B36',
                    },
                }}
            >
                {fmt.icon}
            </IconButton>
        </Tooltip>
    ));

    if (collapsed) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                <Tooltip title={expanded ? 'Hide formatting' : 'Formatting'} arrow>
                    <IconButton
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                        sx={{
                            width: toggleBtnSize,
                            height: toggleBtnSize,
                            borderRadius: toggleBtnRadius,
                            fontSize: toggleBtnFontSize,
                            fontWeight: 600,
                            color: expanded ? '#00A76F' : '#637381',
                            backgroundColor: expanded ? 'rgba(0, 167, 111, 0.08)' : 'transparent',
                            '&:hover': {
                                backgroundColor: 'rgba(145, 158, 171, 0.16)',
                            },
                        }}
                    >
                        Aa
                    </IconButton>
                </Tooltip>
                {expanded && formatButtons}
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
            {formatButtons}
        </Box>
    );
};

FormattingToolbar.propTypes = {
    textareaRef: PropTypes.object.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    isMobile: PropTypes.bool,
    isCompact: PropTypes.bool,
};

FormattingToolbar.defaultProps = {
    isMobile: false,
    isCompact: false,
};

export default FormattingToolbar;
